import './instrument.js' // must load first so Sentry can watch everything below
import './analytics.js'  // sets up GA before the first page view fires
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import { hydrateSyncCache } from './services/storage.js'
import { setupStatusBar } from './services/statusBar.js'
import { IS_NATIVE_APP } from './services/runtime.js'

setupStatusBar()

// Marks the root element so index.css can apply a native-only look (a
// smaller --jai-zoom, etc.) without ever touching the web build's styling.
if (IS_NATIVE_APP) {
  document.documentElement.classList.add('native-app')

  // Lock the page scale. WKWebView zooms the whole page in when a text field
  // it considers too small gets focus ("zoom to focused element") and does
  // not zoom back out on blur — the app stayed stuck zoomed in after asking
  // JAI until a manual double-tap. Capacitor's own zoomEnabled=false only
  // disables the pinch gesture, not that programmatic zoom. WebKit skips the
  // focus-zoom entirely (and double-tap zoom) when the viewport disallows
  // user scaling, which is what maximum-scale=1 + user-scalable=no do here.
  // Native only: mobile Safari ignores these for pinch anyway, and Android
  // Chrome would honour user-scalable=no on the web, which costs real users
  // pinch-zoom accessibility — so index.html's viewport stays as it is.
  document.querySelector('meta[name="viewport"]')?.setAttribute(
    'content',
    'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
  )
}

// Loads the auth token/user (and remembered app path) into the in-memory
// sync-read cache before anything renders — see services/storage.js. A
// no-op on web (already seeded synchronously from localStorage at import
// time); on native this is the one await standing between page load and the
// first render, so the axios interceptor and the first API calls never race
// ahead of a not-yet-loaded token. Renders even if hydration fails — an
// empty cache just means "no saved session", the same as a fresh install,
// which is a safe fallback rather than a blank screen forever.
hydrateSyncCache()
  .catch((err) => console.error('[main] sync cache hydration failed', err))
  .finally(() => {
    createRoot(document.getElementById('root')).render(
      <StrictMode>
        <App />
      </StrictMode>,
    )
  })
